import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, Truck, ArrowRight, Tag, Lock, Building2 } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../lib/cart";
import { retaPromo, B3G1_LABEL } from "../lib/promo";
import { useSiteSettings } from "../lib/site-settings";
import { readReferral } from "../lib/referral";
import { track } from "../lib/gtag";
import { metaTrack, newEventId, getMetaCookies } from "../lib/meta";
import { getAttributionForOrder } from "../lib/attribution";
import { toast } from "sonner";
import PayByBankTrust from "../components/PayByBankTrust";
import PayByBankExplainerModal from "../components/PayByBankExplainerModal";
import GlobalPayCardModal from "../components/GlobalPayCardModal";
import GiftProgressBadge from "../components/GiftProgressBadge";
import PerksLadder from "../components/PerksLadder";
import AddressLookup from "../components/AddressLookup";

export default function Checkout() {
  const { items, subtotal, clear, refreshPrices } = useCart();
  const { free_shipping_threshold } = useSiteSettings();
  const navigate = useNavigate();
  const [shipping, setShipping] = useState({
    title: "", first_name: "", last_name: "",
    full_name: "", organisation: "", email: "", phone: "",
    address_line1: "", address_line2: "", city: "", postcode: "", country: "United Kingdom",
    date_of_birth: "",
  });
  // Postcode lookup state — debounced query against /api/lookup/postcode
  // gives the customer real-time feedback that their postcode is a valid
  // UK record (and shows the area it resolves to). Three states:
  //   null     → not yet checked / too short to lookup
  //   loading  → request in flight
  //   { valid: bool, ... } → result returned
  const [postcodeLookup, setPostcodeLookup] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [optAcknowledged, setOptAcknowledged] = useState(false);
  // Submission validation — populated on submit click (then live-updated
  // as the customer fixes each error). Drives the top-of-form summary
  // panel AND per-field red-border highlighting.
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // PAF verification flag — true once the customer has picked an address
  // from the postcode lookup. Stays true even after manual tweaks so we
  // don't blackball PAF-canonical house names like "Palmyra" or "Walnut
  // Lodge" that lack both a digit and one of our keyword tokens.
  const [pafVerified, setPafVerified] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [discountValid, setDiscountValid] = useState(null);
  const [creditValid, setCreditValid] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // GlobalPay (card) modal — opens when customer picks "Pay by Card"
  // instead of Pay-by-Bank. Modal handles its own auth flow (AVS/OTP/
  // PIN/REDIRECT_URL) and creates the order itself on submit.
  const [showCardModal, setShowCardModal] = useState(false);
  const [notes, setNotes] = useState("");
  const [showPbbModal, setShowPbbModal] = useState(false);
  const [showCardNudge, setShowCardNudge] = useState(false);
  const cartIdRef = useRef(localStorage.getItem("pg_cart_id") || null);
  const trackTimeoutRef = useRef(null);
  const beganCheckoutRef = useRef(false);

  // Fire begin_checkout once when /checkout is first rendered with items
  useEffect(() => {
    if (beganCheckoutRef.current) return;
    if (items.length === 0) return;
    track.beginCheckout(items, subtotal);
    metaTrack.initiateCheckout(items, subtotal);
    beganCheckoutRef.current = true;
  }, [items, subtotal]);

  // Sync cart prices with the live catalogue ON MOUNT.
  //
  // The cart lives in localStorage so a returning visitor can carry a
  // line item whose `price` was set days ago. If we changed the
  // catalogue price in the interim (e.g. £3.99 → £4.99), the cart UI
  // would render the stale £3.99 while the backend, which looks up the
  // current product on /api/orders, charges £4.99 — a trust-breaking
  // mismatch (£8.98 displayed vs £9.98 debited).
  //
  // Refresh once on checkout mount and surface a toast if anything
  // moved so the customer sees the corrected number BEFORE clicking pay.
  const pricesSyncedRef = useRef(false);
  useEffect(() => {
    if (pricesSyncedRef.current) return;
    if (items.length === 0) return;
    pricesSyncedRef.current = true;
    api.get("/products").then(({ data }) => {
      const list = Array.isArray(data) ? data : (data?.products || []);
      const changed = refreshPrices(list);
      if (changed && changed.length > 0) {
        const first = changed[0];
        const arrow = first.now > first.was ? "↑" : "↓";
        toast.message(
          changed.length === 1
            ? `${first.name} price updated: £${Number(first.was).toFixed(2)} ${arrow} £${Number(first.now).toFixed(2)}`
            : `${changed.length} cart prices updated to the latest catalogue.`,
          { description: "Your cart total has been refreshed before checkout." }
        );
      }
    }).catch(() => { /* non-fatal: backend will reject mismatches on submit anyway */ });
  }, []);

  // Postcode lookup: debounce the user's typing and validate against
  // postcodes.io via our proxy. Catches Royal-Mail-unrecognisable
  // postcodes BEFORE the order is paid for (Elif Gulec's SK11 8NA / Mill
  // Street mismatch was the trigger for adding this).
  useEffect(() => {
    const raw = (shipping.postcode || "").trim();
    if (raw.length < 5) {
      setPostcodeLookup(null);
      return;
    }
    setPostcodeLookup("loading");
    const handle = setTimeout(async () => {
      try {
        const enc = encodeURIComponent(raw);
        const { data } = await api.get(`/lookup/postcode/${enc}`);
        setPostcodeLookup(data || { valid: false });
      } catch {
        setPostcodeLookup({ valid: false, error: "network" });
      }
    }, 450);
    return () => clearTimeout(handle);
  }, [shipping.postcode]);


  // Abandoned cart tracking: once the customer has entered a valid email +
  // has items, record it so our scheduler can follow up if they bounce.
  useEffect(() => {
    if (!shipping.email || !shipping.email.includes("@") || items.length === 0) return;
    if (trackTimeoutRef.current) clearTimeout(trackTimeoutRef.current);
    trackTimeoutRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/cart/track", {
          email: shipping.email,
          name: shipping.full_name || null,
          items: items.map((i) => ({
            sku: i.sku, name: i.name, strength: i.strength,
            price: i.price, quantity: i.quantity,
          })),
          subtotal,
          cart_id: cartIdRef.current,
        });
        if (data?.cart_id) {
          cartIdRef.current = data.cart_id;
          localStorage.setItem("pg_cart_id", data.cart_id);
        }
      } catch (err) {
        // Cart-tracking failure isn't user-blocking — the order still
        // completes via the main create-order POST. Surface to console
        // only so we can spot a recurring outage.
        console.debug("[Checkout] abandoned-cart track failed (non-blocking):", err?.message || err);
      }
    }, 1200);
    return () => trackTimeoutRef.current && clearTimeout(trackTimeoutRef.current);
  }, [shipping.email, shipping.full_name, items, subtotal]);

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h1 className="text-[#F7FAFC] text-3xl pg-display mb-4">Your basket is empty</h1>
        <Link to="/products" className="pg-btn-primary">Shop Catalogue <ArrowRight size={16} /></Link>
      </div>
    );
  }

  const update = (k, v) => {
    setShipping((prev) => {
      const next = { ...prev, [k]: v };
      // Keep full_name in sync whenever either part changes — backend
      // still receives the joined value so historical analytics, COA
      // emails, and dispatch labels render unchanged.
      if (k === "first_name" || k === "last_name") {
        next.full_name = `${next.first_name || ""} ${next.last_name || ""}`.replace(/\s+/g, " ").trim();
      }
      return next;
    });
  };

  // Client-side guard: address_line1 must contain a digit (e.g. "12 High
  // Street", "Flat 4") OR a recognised property keyword (e.g. "The Old
  // Vicarage", "Rose Cottage"). Mirrors the backend validator in
  // ShippingAddress so users see a friendly inline error before submitting.
  const hasPropertyIdentifier = (line) => {
    if (!line) return false;
    if (/\d/.test(line)) return true;
    const lower = line.toLowerCase();
    const keywords = [
      "flat ", "apartment", "apt ", "suite ", "unit ",
      "the ", "rose ", "old ", "manor", "cottage", "house",
      "villa", "lodge", "barn", "farm", "hall", "court",
      "mews", "studio", "penthouse", "ground floor", "first floor",
      "second floor", "top floor", "basement", "annexe", "annex",
      "rear ", "front ",
    ];
    return keywords.some((k) => lower.includes(k));
  };

  const applyDiscount = async () => {
    const code = discountCode.trim().replace(/\s+/g, "");
    if (!code) return;
    try {
      const emailQ = shipping.email?.includes("@") ? `email=${encodeURIComponent(shipping.email.trim())}&` : "";
      const { data } = await api.get(`/discounts/validate/${encodeURIComponent(code)}?${emailQ}subtotal=${subtotal.toFixed(2)}`);
      if (data.amount_off && !data.min_spend) {
        // Store-credit voucher — stacks with discounts and B3G1.
        // Covers products only: postage is always charged.
        setCreditValid(data);
        setDiscountCode("");
        toast.success(`£${Number(data.amount_off).toFixed(2)} store credit applied — postage still applies`);
        return;
      }
      if (promoActive) {
        toast.error("Buy 3 Get 1 Free is already applied — discount codes can't be combined with this offer.");
        return;
      }
      setDiscountValid(data);
      setDiscountCode("");
      toast.success(
        data.amount_off
          ? `£${Number(data.amount_off).toFixed(2)} voucher applied`
          : Number(data.percent_off) > 0
          ? `${data.percent_off}% discount applied${data.free_shipping ? " + free delivery" : ""}`
          : "Free delivery applied"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    }
  };

  // Buy 3 Get 1 Free (Retatrutide 20/30) — blocks discount-code stacking.
  const promo = retaPromo(items);
  const promoActive = promo.discount > 0;
  const voucherShort = discountValid?.min_spend ? Math.max(0, Number(discountValid.min_spend) - subtotal) : 0;
  const discountAmount = !promoActive && discountValid && voucherShort <= 0
    ? (discountValid.amount_off ? Math.min(Number(discountValid.amount_off), subtotal) : subtotal * (discountValid.percent_off / 100))
    : 0;
  // Store credit stacks on top of promo/discount and covers items only.
  const preCreditNet = Math.max(0, subtotal - promo.discount - discountAmount);
  const creditAmount = creditValid
    ? Math.min(Number(creditValid.amount_off), preCreditNet)
    : 0;
  const netSubtotal = preCreditNet - creditAmount;
  // Free-shipping eligibility uses the POST-discount (pre-credit) subtotal.
  // Store-credit orders ALWAYS pay postage — credit never covers shipping.
  const codeFreeShip = !promoActive && !!discountValid?.free_shipping;
  const shippingCost = creditValid
    ? 4.99
    : codeFreeShip || preCreditNet >= Number(free_shipping_threshold || 50) ? 0 : 4.99;
  const total = Math.max(0, netSubtotal + shippingCost);

  // Comprehensive client-side validator — returns a map of {field: error}.
  // Drives BOTH the top-of-form error summary panel AND the per-field
  // red-border highlighting. Order of keys matters (used for "fix the
  // first error first" scrolling).
  const validateAll = () => {
    const errs = {};
    if (!shipping.title?.trim()) errs.title = "Please select a title (Mr / Mrs / etc.).";
    if (!shipping.first_name?.trim()) errs.first_name = "First name is required.";
    if (!shipping.last_name?.trim()) errs.last_name = "Last name is required.";
    if (!shipping.email?.trim()) {
      errs.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(shipping.email.trim())) {
      errs.email = "Please enter a valid email address.";
    }
    if (!shipping.address_line1?.trim()) {
      errs.address_line1 = "Address line 1 is required.";
    } else if (!pafVerified && !hasPropertyIdentifier(shipping.address_line1)) {
      errs.address_line1 = "Include a house number, flat number, or property name.";
    }
    if (!shipping.city?.trim()) errs.city = "City / town is required.";
    if (!shipping.postcode?.trim()) {
      errs.postcode = "Postcode is required.";
    } else if (postcodeLookup && postcodeLookup.valid === false) {
      errs.postcode = "Not a valid UK postcode — please double-check it.";
    }
    if (!shipping.date_of_birth?.trim()) {
      errs.date_of_birth = "Date of birth is required (you must be 18+).";
    } else {
      const dob = parseDob(shipping.date_of_birth);
      if (dob === "invalid") {
        errs.date_of_birth = "That doesn't look like a valid date of birth.";
      } else if (!dob) {
        errs.date_of_birth = "Please enter your date of birth as DD/MM/YYYY (e.g. 14/06/1988).";
      } else if (dob.getFullYear() < 1900 || dob.getTime() > Date.now()) {
        errs.date_of_birth = "That doesn't look like a valid date of birth.";
      } else {
        const ageMs = Date.now() - dob.getTime();
        const ageYrs = ageMs / (1000 * 60 * 60 * 24 * 365.25);
        if (ageYrs < 18) errs.date_of_birth = "You must be 18 or over to place an order.";
      }
    }
    if (!acknowledged) errs.acknowledged = "Please confirm the research-use-only declaration.";
    if (!optAcknowledged) errs.optAcknowledged = "Please confirm you have read the Terms & Conditions.";
    return errs;
  };

  // Map each formErrors key → the data-testid of its input, so error
  // summary items can scroll/focus the right field on click. Keeps the
  // mapping in one place rather than scattered through the JSX.
  const FIELD_TESTID = {
    title: "ship-title",
    first_name: "ship-first-name",
    last_name: "ship-last-name",
    email: "ship-email",
    address_line1: "ship-address1",
    city: "ship-city",
    postcode: "ship-postcode",
    date_of_birth: "ship-date-of-birth",
    acknowledged: "rou-checkbox",
    optAcknowledged: "rou-optional-checkbox",
  };

  const jumpToField = (errKey) => {
    const tid = FIELD_TESTID[errKey];
    if (!tid) return;
    const el = document.querySelector(`[data-testid="${tid}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    // Focus once the scroll settles so mobile keyboards don't pop too early.
    setTimeout(() => {
      try { el.focus({ preventScroll: true }); } catch (err) { /* iOS Safari */ }
    }, 350);
  };

  // Derived — automatically updates as the customer corrects each field
  // because validateAll() runs every render. No useState/useEffect needed.
  const formErrors = submitAttempted ? validateAll() : {};

  const submit = async (e) => {
    e.preventDefault();
    const errs = validateAll();
    setSubmitAttempted(true);
    if (Object.keys(errs).length > 0) {
      // Scroll to the error summary panel so the customer sees exactly
      // what they need to fix — far better UX than one-at-a-time toasts.
      // Use setTimeout to wait for the panel to render after state update.
      setTimeout(() => {
        const panel = document.getElementById("checkout-error-summary");
        if (panel) panel.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return;
    }
    // First-time Pay-by-Bank user? Show explainer modal before redirecting
    // to Fena. Once they've seen it (or completed an order), skip straight through.
    let seen = false;
    try { seen = localStorage.getItem("pg_pbb_explainer_seen") === "1"; } catch (err) {
      // localStorage can throw in private-browsing modes — treat as "not seen"
      // and let the explainer modal fire (safer default).
      console.warn("[Checkout] localStorage read failed:", err?.message || err);
    }
    if (!seen) {
      setShowPbbModal(true);
      return;
    }
    await proceedToPayment();
  };

  const proceedToPayment = async () => {
    setShowPbbModal(false);
    setSubmitting(true);
    try {
      // Generate a UUID used as Pixel `eventID` on the Purchase event
      // and `event_id` on the matching server-side Meta CAPI event, so
      // Meta deduplicates the two when the Fena webhook fires the
      // server copy. Cookies (_fbp / _fbc) are forwarded so server
      // attribution links back to the ad click.
      const meta_event_id = newEventId();
      const { fbp: meta_fbp, fbc: meta_fbc } = getMetaCookies();
      const { data: order } = await api.post("/orders", {
        items: items.map((i) => ({
          product_id: i.product_id, sku: i.sku, name: i.name,
          strength: i.strength, price: i.price, quantity: i.quantity,
        })),
        shipping: { ...shipping, paf_verified: pafVerified },
        research_use_only_acknowledged: true,
        notes,
        discount_code: promoActive ? null : (discountValid ? discountValid.code : null),
        credit_code: creditValid ? creditValid.code : null,
        referral_code: readReferral(),
        meta_event_id,
        meta_fbp,
        meta_fbc,
        ...getAttributionForOrder(),
      });
      const { data: pay } = await api.post("/payments/fena/initiate", { order_id: order.id });
      // Mark cart completed so scheduler doesn't send abandonment emails
      if (cartIdRef.current) {
        api.post("/cart/complete", { cart_id: cartIdRef.current }).catch(() => {});
        localStorage.removeItem("pg_cart_id");
      }
      clear();
      // Live Fena: redirect to Fena's hosted bank-picker URL.
      // Mocked: redirect_url is a relative path /checkout/secure-payment?...
      if (pay.redirect_url && /^https?:\/\//i.test(pay.redirect_url)) {
        window.location.href = pay.redirect_url;
        return;
      }
      navigate(`/checkout/secure-payment?order=${order.order_number}&id=${order.id}&ref=${pay.fena_reference}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // FastAPI returns Pydantic validation errors as a structured array of
      // {loc, msg, ...} objects with HTTP 422. A plain string for
      // application-level rejections (insufficient stock etc.) with HTTP 400.
      // Both used to fall through to a generic "Could not place order" which
      // hid the actual reason from the customer — show the real message now.
      let msg = "Could not place order";
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Strip Pydantic v2's "Value error, " prefix so the customer reads
        // "You must be 18 or over to place an order on this website."
        // not "Value error, You must be 18 or over…"
        msg = detail
          .map((err) => String(err?.msg || "").replace(/^Value error,\s*/i, ""))
          .filter(Boolean)
          .join(" · ") || msg;
      }
      toast.error(msg);
      console.error("[Checkout] place-order failed:", e.response?.status, detail);
    } finally {
      setSubmitting(false);
    }
  };

  /** Builds the order payload that we hand off to the GlobalPay card modal.
   * Modal calls /api/orders itself so we don't create a duplicate order if
   * the customer toggles between Pay-by-Bank and Pay-by-Card. */
  const buildOrderPayload = () => {
    const meta_event_id = newEventId();
    const { fbp: meta_fbp, fbc: meta_fbc } = getMetaCookies();
    return {
      items: items.map((i) => ({
        product_id: i.product_id, sku: i.sku, name: i.name,
        strength: i.strength, price: i.price, quantity: i.quantity,
      })),
      shipping: { ...shipping, paf_verified: pafVerified },
      research_use_only_acknowledged: true,
      notes,
      discount_code: promoActive ? null : (discountValid ? discountValid.code : null),
      credit_code: creditValid ? creditValid.code : null,
      referral_code: readReferral(),
      meta_event_id,
      meta_fbp,
      meta_fbc,
      ...getAttributionForOrder(),
    };
  };

  /** Click handler for the "Pay by Card" button. Reuses checkout
   * validation, then opens the card modal. */
  const handlePayByCardClick = () => {
    const errs = validateAll();
    setSubmitAttempted(true);
    if (Object.keys(errs).length > 0) {
      setTimeout(() => {
        const panel = document.getElementById("checkout-error-summary");
        if (panel) panel.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      toast.error("Please complete the shipping details before paying.");
      return;
    }
    setShowCardNudge(true);
  };

  const handleCardPaid = (orderNumber, orderId, opts = {}) => {
    // Mark cart complete same way Pay-by-Bank flow does, then navigate.
    // opts.verifying === true means the modal handed the customer off
    // because the gateway sat on PENDING too long. Same destination —
    // the secure-payment page is happy to keep polling until the
    // gateway settles.
    if (cartIdRef.current) {
      api.post("/cart/complete", { cart_id: cartIdRef.current }).catch(() => {});
      try { localStorage.removeItem("pg_cart_id"); } catch (_) { /* private mode */ }
    }
    clear();
    const verifyingFlag = opts.verifying ? "&verifying=1" : "";
    navigate(`/checkout/secure-payment?order=${orderNumber}&id=${orderId}&via=card${verifyingFlag}`);
  };

  // 3DS handoff — commit the cart then navigate same-tab to the bank's
  // verification page (popups get blocked, especially on mobile Safari).
  const handleCard3DSRedirect = (orderNumber, orderId, url) => {
    // CRITICAL: persist the in-flight order BEFORE we leave the site. GlobalPay's
    // 3DS return can land on a bare redirect URL with our query params stripped —
    // if that happens the return page recovers the order from here (same-origin
    // localStorage survives the round-trip to the gateway and back). Without this
    // the customer gets dumped on the homepage after completing 3DS.
    try {
      localStorage.setItem("pg_pending_card_order", JSON.stringify({
        order_number: orderNumber, id: orderId, ts: Date.now(),
      }));
    } catch (_) { /* private mode */ }
    if (cartIdRef.current) {
      api.post("/cart/complete", { cart_id: cartIdRef.current }).catch(() => {});
      try { localStorage.removeItem("pg_cart_id"); } catch (_) { /* private mode */ }
    }
    clear();
    window.location.assign(url);
  };

  return (
    <form onSubmit={submit} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="checkout-page">
      <div className="pg-micro mb-3">// Checkout</div>
      <h1 className="text-[#F7FAFC] text-4xl pg-display mb-10" style={{ fontFamily: "'Exo 2'" }}>Secure UK Checkout</h1>

      {/* Error summary — populated after a submit attempt with anything
          the customer still needs to fix. Clears entries reactively as
          they correct each one. Scrolled into view by the submit handler
          so it's impossible to miss. */}
      {submitAttempted && Object.keys(formErrors).length > 0 && (
        <div
          id="checkout-error-summary"
          role="alert"
          aria-live="polite"
          className="mb-8 border border-[#FF8A5B] bg-[#FF8A5B]/[0.08] rounded-sm p-5"
          data-testid="checkout-error-summary"
        >
          <div className="flex items-start gap-3">
            <div className="text-[#FF8A5B] text-lg leading-none pt-0.5">!</div>
            <div className="flex-1">
              <div className="text-[#FF8A5B] pg-heading text-base mb-1" style={{ fontFamily: "'Rajdhani'" }}>
                Please fix {Object.keys(formErrors).length === 1 ? "this" : `these ${Object.keys(formErrors).length} items`} before paying:
              </div>
              <ul className="text-sm text-[#F7FAFC] space-y-1.5">
                {Object.entries(formErrors).map(([key, msg]) => (
                  <li key={key}>
                    <button
                      type="button"
                      onClick={() => jumpToField(key)}
                      className="text-left w-full hover:text-[#FF8A5B] hover:underline transition-colors flex items-start gap-2"
                      data-testid={`error-${key}`}
                    >
                      <span className="text-[#FF8A5B] shrink-0">&rsaquo;</span>
                      <span>{msg} <span className="text-[10px] text-[#9CA8B8] uppercase tracking-wider ml-1">tap to fix</span></span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 space-y-6">
          <div className="pg-card p-6">
            <div className="pg-micro mb-4">// Shipping Details</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Title" required>
                <select
                  value={shipping.title}
                  onChange={(e) => update("title", e.target.value)}
                  className={`pg-input ${formErrors.title ? "border-[#FF8A5B] border-2" : ""}`}
                  data-testid="ship-title"
                >
                  <option value="">Select…</option>
                  <option value="Mr">Mr</option>
                  <option value="Mrs">Mrs</option>
                  <option value="Ms">Ms</option>
                  <option value="Miss">Miss</option>
                  <option value="Dr">Dr</option>
                  <option value="Prof">Prof</option>
                  <option value="Mx">Mx</option>
                </select>
              </Field>
              <div className="hidden sm:block"></div>
              <Field label="First name" required><input autoComplete="given-name" value={shipping.first_name} onChange={(e) => update("first_name", e.target.value)} className={`pg-input ${formErrors.first_name ? "border-[#FF8A5B] border-2" : ""}`} data-testid="ship-first-name" /></Field>
              <Field label="Last name" required><input autoComplete="family-name" value={shipping.last_name} onChange={(e) => update("last_name", e.target.value)} className={`pg-input ${formErrors.last_name ? "border-[#FF8A5B] border-2" : ""}`} data-testid="ship-last-name" /></Field>
              <Field label="Organisation"><input value={shipping.organisation} onChange={(e) => update("organisation", e.target.value)} className="pg-input" data-testid="ship-organisation" /></Field>
              <Field label="Email" required><input type="email" value={shipping.email} onChange={(e) => update("email", e.target.value)} className={`pg-input ${formErrors.email ? "border-[#FF8A5B] border-2" : ""}`} data-testid="ship-email" /></Field>
              <Field label="Phone"><input value={shipping.phone} onChange={(e) => update("phone", e.target.value)} className="pg-input" data-testid="ship-phone" /></Field>
              <div className="sm:col-span-2">
                <AddressLookup
                  defaultPostcode={shipping.postcode}
                  onSelect={(addr) => {
                    setShipping((prev) => ({
                      ...prev,
                      address_line1: addr.address_line1 || "",
                      address_line2: addr.address_line2 || "",
                      city: addr.city || "",
                      postcode: addr.postcode || "",
                      country: addr.country || "United Kingdom",
                    }));
                    // Royal Mail has confirmed this is a deliverable address;
                    // skip the "must contain a number/keyword" heuristic so
                    // pure house names like "Palmyra" or "Walnut Lodge" go
                    // through without nagging the customer.
                    setPafVerified(true);
                  }}
                />
              </div>
              <div className="sm:col-span-2">
                <Field label="Address line 1" required>
                  <input
                    autoComplete="address-line1"
                    value={shipping.address_line1}
                    onChange={(e) => update("address_line1", e.target.value)}
                    placeholder="e.g. 12 High Street, or Flat 4 Sunset Apartments"
                    aria-describedby="address-line1-help"
                    className={`pg-input ${
                      formErrors.address_line1 || (!pafVerified && shipping.address_line1 && !hasPropertyIdentifier(shipping.address_line1))
                        ? "border-[#FF8A5B] border-2"
                        : ""
                    }`}
                    data-testid="ship-address1"
                  />
                </Field>
                {!pafVerified && shipping.address_line1 && !hasPropertyIdentifier(shipping.address_line1) ? (
                  <div
                    id="address-line1-help"
                    className="text-[10px] text-[#FF8A5B] mt-1 leading-relaxed"
                    data-testid="address1-warning"
                  >
                    Please include a house number, flat number, or property name so Royal Mail can deliver.
                  </div>
                ) : (
                  <div id="address-line1-help" className="text-[10px] text-[#9CA8B8] mt-1 leading-relaxed">
                    Include your house number, flat number, or property name (e.g. "12" or "Flat 4").
                  </div>
                )}
              </div>
              <div className="sm:col-span-2"><Field label="Address line 2"><input value={shipping.address_line2} onChange={(e) => update("address_line2", e.target.value)} className="pg-input" data-testid="ship-address2" /></Field></div>
              <Field label="City" required><input value={shipping.city} onChange={(e) => update("city", e.target.value)} autoComplete="address-level2" className={`pg-input ${formErrors.city ? "border-[#FF8A5B] border-2" : ""}`} data-testid="ship-city" /></Field>
              <div>
                <Field label="Postcode" required>
                  <input
                    autoComplete="postal-code"
                    value={shipping.postcode}
                    onChange={(e) => update("postcode", e.target.value)}
                    placeholder="e.g. SK11 8NA"
                    className={`pg-input ${
                      formErrors.postcode || (postcodeLookup && postcodeLookup.valid === false)
                        ? "border-[#FF8A5B] border-2"
                        : postcodeLookup && postcodeLookup.valid === true
                          ? "border-[#A6FF00]/40"
                          : ""
                    }`}
                    data-testid="ship-postcode"
                    aria-describedby="postcode-help"
                  />
                </Field>
                <div id="postcode-help" className="text-[10px] mt-1 leading-relaxed min-h-[14px]">
                  {postcodeLookup === "loading" && (
                    <span className="text-[#9CA8B8]">Checking postcode…</span>
                  )}
                  {postcodeLookup && postcodeLookup.valid === true && (
                    <span className="text-[#A6FF00]" data-testid="postcode-valid">
                      ✓ Valid · {postcodeLookup.region || postcodeLookup.country}
                      {postcodeLookup.city && postcodeLookup.city !== postcodeLookup.county
                        ? ` (${postcodeLookup.city})`
                        : ""}
                    </span>
                  )}
                  {postcodeLookup && postcodeLookup.valid === false && (
                    <span className="text-[#FF8A5B]" data-testid="postcode-invalid">
                      ✗ Not a valid UK postal record — please double-check.
                    </span>
                  )}
                </div>
              </div>
              <div className="sm:col-span-2">
                <Field label="Date of birth (must be 18 or over)" required>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="bday"
                    placeholder="DD/MM/YYYY"
                    maxLength={10}
                    value={shipping.date_of_birth}
                    onChange={(e) => update("date_of_birth", formatDobInput(e.target.value))}
                    className={`pg-input ${formErrors.date_of_birth ? "border-[#FF8A5B] border-2" : ""}`}
                    data-testid="ship-date-of-birth"
                    aria-describedby="dob-help"
                  />
                </Field>
                {formErrors.date_of_birth && (
                  <div
                    className="text-xs text-[#FF8A5B] mt-1.5 font-medium"
                    role="alert"
                    data-testid="ship-date-of-birth-error"
                  >
                    &uarr; {formErrors.date_of_birth}
                  </div>
                )}
                <div id="dob-help" className="text-[10px] text-[#9CA8B8] mt-1 leading-relaxed">
                  Type it as day / month / year, e.g. 14/06/1988. Peptigen Labs supplies laboratory research materials to qualified researchers aged 18 or over only.
                  By providing your date of birth you confirm this declaration is true. False declarations may
                  constitute a criminal offence and will void any contract under our Terms.
                </div>
              </div>
            </div>
          </div>

          {/* Payment - generic Secure Bank Transfer (no third-party brand exposed) */}
          <div className="pg-card p-6">
            <div className="pg-micro mb-4">// Payment Method</div>
            <div className="p-5 border border-[#A6FF00] bg-[#A6FF00]/[0.06] rounded-sm shadow-[0_0_24px_rgba(166,255,0,0.18)]" data-testid="payment-method-panel">
              <div className="flex items-start gap-3">
                <Lock size={22} className="text-[#A6FF00] mt-1 shrink-0" />
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1 flex-wrap">
                    <span className="text-[#F7FAFC] pg-heading text-xl" style={{ fontFamily: "'Rajdhani'" }}>
                      Secure UK Bank Transfer
                    </span>
                    <span className="pg-badge-stock">Recommended</span>
                  </div>
                  <div className="text-[#F7FAFC] font-medium mb-1">Pay directly from your UK bank — no card details required.</div>
                  <div className="text-[#9CA8B8] text-sm">
                    Authenticated by your bank's own security (Open Banking). Bank-grade encryption end to end. Fast, free, and instantly confirmed.
                  </div>
                </div>
                <ShieldCheck size={22} className="text-[#A6FF00] hidden sm:block" />
              </div>
              <div className="mt-4 pt-4 border-t border-[#A6FF00]/20 grid grid-cols-3 gap-3 text-[10px] pg-mono uppercase tracking-[0.18em] text-[#9CA8B8]">
                <span className="flex items-center gap-1.5"><Lock size={10} className="text-[#5EEAD4]" /> 256-bit Encrypted</span>
                <span className="flex items-center gap-1.5"><Building2 size={10} className="text-[#5EEAD4]" /> FCA Regulated</span>
                <span className="flex items-center gap-1.5"><ShieldCheck size={10} className="text-[#5EEAD4]" /> Open Banking</span>
              </div>
            </div>
            <p className="mt-3 text-xs text-[#9CA8B8] leading-relaxed">
              We never see or store your bank credentials. Authorisation happens within your bank's own secure environment.
            </p>

            {/* Conversion-rate trust block — explains Pay by Bank for first-time
                Open Banking users (FCA badge, 4-step expander, bank logos). */}
            <PayByBankTrust />
          </div>

          {/* Compliance */}
          <div className="pg-card p-6">
            <div className="pg-micro mb-4">// Research-Use-Only Acknowledgement</div>
            <p className="text-[#9CA8B8] text-sm leading-relaxed mb-4">
              Peptigen Labs does not provide guidance on human use, dosage, administration, reconstitution, or therapeutic outcomes.
            </p>
            <label
              className={`flex items-start gap-3 p-4 border rounded-sm cursor-pointer transition-colors ${
                formErrors.acknowledged
                  ? "border-[#FF8A5B] border-2 bg-[#FF8A5B]/[0.06]"
                  : "border-[#A6FF00]/40 bg-[#A6FF00]/[0.05]"
              }`}
              data-testid="rou-checkbox-label"
            >
              <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} className="mt-1 accent-[#A6FF00]" data-testid="rou-checkbox" />
              <span className="text-sm text-[#F7FAFC]">
                I confirm that I am 18+ and that these products are being purchased for laboratory research use only and are not intended for human consumption.
                <span className="text-[#A6FF00]"> *</span>
              </span>
            </label>
            <label
              className={`mt-3 flex items-start gap-3 p-4 border rounded-sm cursor-pointer transition-colors ${
                formErrors.optAcknowledged
                  ? "border-[#FF8A5B] border-2 bg-[#FF8A5B]/[0.06]"
                  : "border-[#00C2D6]/30"
              }`}
              data-testid="rou-optional-checkbox-label"
            >
              <input type="checkbox" checked={optAcknowledged} onChange={(e) => setOptAcknowledged(e.target.checked)} className="mt-1 accent-[#00C2D6]" data-testid="rou-optional-checkbox" />
              <span className="text-sm text-[#9CA8B8]">
                I understand Peptigen Labs products are supplied strictly for research use only and that I have read the <Link to="/legal/terms" className="text-[#00C2D6] hover:underline">Terms & Conditions</Link>.
                <span className="text-[#A6FF00]"> *</span>
              </span>
            </label>
          </div>

          <div className="pg-card p-6">
            <div className="pg-micro mb-3">// Order Notes (optional)</div>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="pg-input" placeholder="Add notes for the dispatch team..." data-testid="checkout-notes" />
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="pg-card p-6 sticky top-24">
            <div className="pg-micro mb-4">// Order Summary</div>
            {!creditValid && <PerksLadder subtotal={netSubtotal} />}
            <div className="space-y-3 mb-5">
              {items.map((i) => (
                <div key={i.sku} className="flex justify-between text-sm">
                  <span className="text-[#F7FAFC]">{i.name} {i.strength} <span className="text-[#9CA8B8]">× {i.quantity}</span></span>
                  <span className="text-[#F7FAFC] pg-mono">£{(i.price * i.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>

            <div className="pg-divider mb-4" />

            <div className="mb-4">
              <div className="pg-micro mb-2">Discount / Credit Code</div>
              {promoActive && (
                <div className="text-[12px] text-[#9CA8B8] p-3 rounded-sm border border-[#A6FF00]/30 bg-[#A6FF00]/[0.05] mb-2" data-testid="discount-blocked-note">
                  <span className="text-[#A6FF00] font-semibold">Buy 3 Get 1 Free applied</span> — discount codes can't be combined with this offer. Store-credit vouchers still work.
                </div>
              )}
              <div className="flex gap-2">
                <input value={discountCode} onChange={(e) => setDiscountCode(e.target.value.toUpperCase())} placeholder="Enter code" className="pg-input flex-1 text-sm" data-testid="discount-code-input" />
                <button type="button" onClick={applyDiscount} className="pg-btn-ghost text-xs px-4" data-testid="discount-apply">
                  <Tag size={14} /> Apply
                </button>
              </div>
            </div>

            <div className="space-y-2 text-sm mb-4">
              <Row label="Subtotal" value={`£${subtotal.toFixed(2)}`} />
              {promoActive && <Row label={`${B3G1_LABEL} (${promo.freeCount} free)`} value={`−£${promo.discount.toFixed(2)}`} accent />}
              {!promoActive && promo.remainder === 3 && (
                <div className="p-3 rounded-sm border border-[#A6FF00]/40 bg-[#A6FF00]/[0.07] text-[#D5DCE8] text-xs" data-testid="reta-promo-auto-add">
                  <span className="text-[#A6FF00] font-bold">Buy 3 Get 1 Free:</span> you've bought 3 Retatrutide — your <strong className="text-[#F7FAFC]">free 4th vial will be added to this order automatically</strong> at no extra cost.
                </div>
              )}
              {!promoActive && discountValid && discountAmount > 0 && <Row label={`${discountValid.amount_off ? "Voucher" : "Discount"} (${discountValid.code})`} value={`−£${discountAmount.toFixed(2)}`} accent />}
              {!promoActive && discountValid && voucherShort > 0 && (
                <div className="p-3 rounded-sm border border-[#FF8A5B]/50 bg-[#FF8A5B]/[0.07] text-[#D5DCE8] text-xs" data-testid="voucher-min-spend-warning">
                  <span className="text-[#FF8A5B] font-bold">{discountValid.code}:</span> add <strong className="text-[#F7FAFC]">£{voucherShort.toFixed(2)}</strong> more in items to unlock your £{Number(discountValid.amount_off).toFixed(2)} off (needs £{Number(discountValid.min_spend).toFixed(2)} before delivery).
                </div>
              )}
              {creditValid && <Row label={`Store credit (${creditValid.code})`} value={`−£${creditAmount.toFixed(2)}`} accent data-testid="credit-applied-row" />}
              <Row label={codeFreeShip ? `Shipping (${discountValid.code})` : "Shipping"} value={shippingCost === 0 ? "Free" : `£${shippingCost.toFixed(2)}`} accent={codeFreeShip} />
            </div>
            <div className="pg-divider mb-4" />
            <div className="flex justify-between items-end mb-6">
              <span className="text-[#F7FAFC] pg-heading text-lg">Total</span>
              <span className="text-[#A6FF00] pg-display text-3xl" style={{ fontFamily: "'Exo 2'" }} data-testid="checkout-total">
                £{total.toFixed(2)}
              </span>
            </div>

            <div className="mb-2 text-center pg-mono text-[10px] uppercase tracking-[0.18em] text-[#5EEAD4]">
              Secure UK Bank Transfer · Powered by Fena · FCA-authorised
            </div>
            {/* Repeat the error summary right above the Pay button so a
                customer who scrolled all the way down sees exactly what's
                missing before clicking — no need to scroll back up. */}
            {submitAttempted && Object.keys(formErrors).length > 0 && (
              <div
                role="alert"
                aria-live="polite"
                className="mb-4 border border-[#FF8A5B] bg-[#FF8A5B]/[0.08] rounded-sm p-4"
                data-testid="checkout-error-summary-bottom"
              >
                <div className="flex items-start gap-3">
                  <div className="text-[#FF8A5B] text-lg leading-none pt-0.5">!</div>
                  <div className="flex-1">
                    <div className="text-[#FF8A5B] pg-heading text-sm mb-1" style={{ fontFamily: "'Rajdhani'" }}>
                      Fix {Object.keys(formErrors).length === 1 ? "this" : `these ${Object.keys(formErrors).length} items`} before paying:
                    </div>
                    <ul className="text-xs text-[#F7FAFC] space-y-1">
                      {Object.entries(formErrors).map(([key, msg]) => (
                        <li key={key}>
                          <button
                            type="button"
                            onClick={() => jumpToField(key)}
                            className="text-left w-full hover:text-[#FF8A5B] hover:underline transition-colors flex items-start gap-2"
                            data-testid={`error-bottom-${key}`}
                          >
                            <span className="text-[#FF8A5B] shrink-0">&rsaquo;</span>
                            <span>{msg} <span className="text-[10px] text-[#9CA8B8] uppercase tracking-wider ml-1">tap to fix</span></span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
            <button type="submit" disabled={submitting} className="pg-btn-checkout" data-testid="place-order-fena-btn">
              <Lock size={18} />
              {submitting ? "Processing..." : "Pay Securely with Bank Transfer"}
            </button>
            <p className="mt-3 text-center text-xs text-[#9CA8B8] leading-relaxed" data-testid="payee-confirm-tip">
              Your bank may ask you to confirm a new payee the first time &mdash; that&apos;s normal, just approve it.
            </p>

            {/* Alternative — Pay by Card (GlobalPay V3).
                ───────────────────────────────────────────
                Gated on REACT_APP_ENABLE_CARD_PAYMENTS. Set to "true" on
                preview for sandbox testing; leave unset on live until the
                gateway is activated by GlobalPay. */}
            {process.env.REACT_APP_ENABLE_CARD_PAYMENTS === "true" && (
              <>
                <div className="mt-4 flex items-center gap-3 text-[10px] uppercase tracking-[0.18em] text-[#9CA8B8] pg-mono">
                  <span className="flex-1 h-px bg-[#1E2E44]" />
                  <span>or</span>
                  <span className="flex-1 h-px bg-[#1E2E44]" />
                </div>
                <button
                  type="button"
                  onClick={handlePayByCardClick}
                  disabled={submitting}
                  className="mt-4 w-full border border-[#5EEAD4]/40 text-[#5EEAD4] hover:bg-[#5EEAD4]/10 transition-colors py-3 px-4 rounded-sm flex items-center justify-center gap-2 text-sm tracking-[0.12em] uppercase font-medium pg-mono"
                  data-testid="place-order-card-btn"
                >
                  <Lock size={14} /> Pay by Card · Visa · Mastercard
                </button>
              </>
            )}
            <div className="mt-3 text-center pg-mono text-[10px] uppercase tracking-[0.22em] text-[#9CA8B8]">
              Order before 14:00 — next-day UK delivery
            </div>
            <div className="mt-2 text-center text-[11px] text-[#9CA8B8]">
              First time paying by bank?{" "}
              <Link to="/help/pay-by-bank" className="text-[#5EEAD4] hover:text-[#A6FF00] underline-offset-2 hover:underline" data-testid="checkout-pay-by-bank-help-link">
                See how it works
              </Link>
            </div>
          </div>
        </div>
      </div>
      <PayByBankExplainerModal
        open={showPbbModal}
        onConfirm={proceedToPayment}
        onClose={() => setShowPbbModal(false)}
      />
      {/* Bank-first nudge — shown before the card modal opens. */}
      {showCardNudge && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="card-nudge-modal">
          <div className="pg-card p-6 max-w-md w-full space-y-4">
            <div className="pg-micro text-[#5EEAD4]">// Before you pay by card</div>
            <h3 className="text-[#F7FAFC] text-xl" style={{ fontFamily: "'Rajdhani'", fontWeight: 700 }}>
              Pay-by-Bank is faster — and helps keep our prices low
            </h3>
            <ul className="text-[#9CA8B8] text-sm space-y-2">
              <li className="flex gap-2"><span className="text-[#A6FF00]">✓</span> Instant, approved in your own banking app — no card details to type</li>
              <li className="flex gap-2"><span className="text-[#A6FF00]">✓</span> Bank-level security on every payment</li>
              <li className="flex gap-2"><span className="text-[#A6FF00]">✓</span> Much lower processing fees — every bank payment helps us keep prices down</li>
            </ul>
            <button
              type="button"
              className="pg-btn-checkout w-full"
              onClick={() => { setShowCardNudge(false); setShowPbbModal(true); }}
              data-testid="card-nudge-use-bank-btn"
            >
              Use Pay-by-Bank instead
            </button>
            <button
              type="button"
              className="w-full border border-[#5EEAD4]/40 text-[#5EEAD4] hover:bg-[#5EEAD4]/10 transition-colors py-2.5 px-4 rounded-sm text-sm tracking-[0.1em] uppercase pg-mono"
              onClick={() => { setShowCardNudge(false); setShowCardModal(true); }}
              data-testid="card-nudge-continue-card-btn"
            >
              Continue with card
            </button>
          </div>
        </div>
      )}
      {/* Card modal only mounts when the feature flag is on. Keeps the
          card-payment code path completely dormant until you flip the flag. */}
      {process.env.REACT_APP_ENABLE_CARD_PAYMENTS === "true" && (
        <GlobalPayCardModal
          open={showCardModal}
          onClose={() => setShowCardModal(false)}
          amount={total}
          orderPayload={buildOrderPayload()}
          onPaid={handleCardPaid}
          onRedirect3DS={handleCard3DSRedirect}
        />
      )}
    </form>
  );
}

// Typed DOB: digits only, slashes inserted automatically → "DD/MM/YYYY".
// Replaces <input type="date">, which in-app browsers (Outlook, Instagram,
// Facebook) often fail to open at all, and which on Android opened on today's
// date with 18+ years greyed out — both read as "it won't let me enter it".
function formatDobInput(raw) {
  const d = (raw || "").replace(/\D/g, "").slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

// Accepts DD/MM/YYYY (what the field produces) or ISO YYYY-MM-DD (older saved
// drafts). Returns a Date when the calendar date exists, "invalid" when the
// format is right but the date isn't (31/02), null when the format is wrong.
function parseDob(value) {
  const s = (value || "").trim();
  let d, m, y;
  let match = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (match) [, d, m, y] = match;
  else if ((match = s.match(/^(\d{4})-(\d{2})-(\d{2})$/))) [, y, m, d] = match;
  else return null;
  const dt = new Date(Number(y), Number(m) - 1, Number(d));
  const valid = dt.getFullYear() === Number(y) && dt.getMonth() === Number(m) - 1 && dt.getDate() === Number(d);
  return valid ? dt : "invalid";
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="pg-micro mb-2">{label} {required && <span className="text-[#A6FF00]">*</span>}</div>
      {children}
    </label>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex justify-between">
      <span className="text-[#9CA8B8]">{label}</span>
      <span className={accent ? "text-[#A6FF00] pg-mono" : "text-[#F7FAFC] pg-mono"}>{value}</span>
    </div>
  );
}
